import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  getArcadeCompetitionsPayload,
  isArcadeCompetitionsApiSuccess,
  listArcadeCompetitions,
  patchArcadeCompetition,
} from '../../services/arcadeCompetitionsApi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

const PAGE_SIZE = 50;
const PATCH_DEBOUNCE_MS = 400;

const MATCH_TOOL_LINKS = [
  { label: 'Prediction questions', path: '/admin/arcade/prediction-questions' },
  { label: 'Prediction candidates', path: '/admin/arcade/prediction-candidates' },
  { label: 'FanWars lineup', path: '/admin/arcade/fanwars-lineup' },
  { label: 'Extend participation', path: '/admin/arcade/participation-force-active' },
  { label: 'Super Over daily', path: '/admin/arcade/super-over-daily' },
  { label: 'Super Over league', path: '/admin/arcade/mind-cricket-league' },
  { label: 'Game catalog', path: '/admin/arcade/game-catalog' },
];

function mergeItemIntoList(items, updatedItem) {
  if (!updatedItem?.cid) return items;
  return items.map((row) => (Number(row.cid) === Number(updatedItem.cid) ? { ...row, ...updatedItem } : row));
}

function ArcadeCompetitions() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [listData, setListData] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [enabledOnly, setEnabledOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [helpExpanded, setHelpExpanded] = useState(false);
  const [savingCids, setSavingCids] = useState({});
  const [toolsAnchor, setToolsAnchor] = useState(null);
  const [toolsCid, setToolsCid] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const patchTimersRef = useRef({});
  const pendingPatchRef = useRef({});

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSessionExpired = useCallback(
    (res) => {
      if (String(res?.statusCode || '') === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return true;
      }
      return false;
    },
    [showSnackbar]
  );

  const handleApiError = useCallback(
    (res, fallback) => {
      if (handleSessionExpired(res)) return true;
      if (!isArcadeCompetitionsApiSuccess(res)) {
        setError(res?.message || fallback);
        return true;
      }
      return false;
    },
    [handleSessionExpired]
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listArcadeCompetitions({
        search: search || undefined,
        enabledOnly,
        limit: PAGE_SIZE,
        offset,
      });
      if (handleApiError(res, 'Failed to load arcade competitions.')) {
        setListData(null);
        return;
      }
      setListData(getArcadeCompetitionsPayload(res));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load arcade competitions.');
      setListData(null);
    } finally {
      setLoading(false);
    }
  }, [enabledOnly, handleApiError, offset, search]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    return () => {
      Object.values(patchTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const items = listData?.items || [];
  const pagination = listData?.pagination || {};
  const total = pagination.total ?? 0;
  const hasMore = offset + items.length < total;
  const hasPrev = offset > 0;

  const schedulerNotes = listData?.scheduler_notes;
  const defaultIplCid = listData?.default_ipl_cid;
  const enabledCids = listData?.enabled_competition_cids || [];
  const mindgameCids = listData?.mindgame_enabled_competition_cids || [];

  const summaryChips = useMemo(
    () => (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {defaultIplCid != null && (
          <Chip size="small" variant="outlined" label={`IPL default CID: ${defaultIplCid}`} />
        )}
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={`Prediction/FanWars enabled: ${enabledCids.length}`}
        />
        <Chip
          size="small"
          color="secondary"
          variant="outlined"
          label={`Mind Cricket enabled: ${mindgameCids.length}`}
        />
      </Box>
    ),
    [defaultIplCid, enabledCids.length, mindgameCids.length]
  );

  const setSavingForCid = (cid, active) => {
    setSavingCids((prev) => {
      const next = { ...prev };
      if (active) next[cid] = (next[cid] || 0) + 1;
      else next[cid] = Math.max(0, (next[cid] || 1) - 1);
      if (next[cid] <= 0) delete next[cid];
      return next;
    });
  };

  const flushPatch = useCallback(
    async (cid) => {
      const body = pendingPatchRef.current[cid];
      if (!body || Object.keys(body).length === 0) return;
      delete pendingPatchRef.current[cid];

      setSavingForCid(cid, true);
      try {
        const res = await patchArcadeCompetition(cid, body);
        if (handleSessionExpired(res)) return;
        if (!isArcadeCompetitionsApiSuccess(res)) {
          showSnackbar(res?.message || 'Failed to update competition settings.', 'error');
          await fetchList();
          return;
        }
        const data = getArcadeCompetitionsPayload(res);
        const updated = data?.item;
        const syncCount = data?.sync?.matches_updated;
        if (updated) {
          setListData((prev) =>
            prev
              ? {
                  ...prev,
                  items: mergeItemIntoList(prev.items || [], updated),
                }
              : prev
          );
        }
        const syncMsg =
          syncCount != null ? ` (${syncCount} arcade match row${syncCount === 1 ? '' : 's'} synced)` : '';
        showSnackbar(`Competition ${cid} updated${syncMsg}.`);
      } catch (patchError) {
        showSnackbar(patchError.message || 'Failed to update competition settings.', 'error');
        await fetchList();
      } finally {
        setSavingForCid(cid, false);
      }
    },
    [fetchList, handleSessionExpired, showSnackbar]
  );

  const schedulePatch = useCallback(
    (cid, field, value) => {
      setListData((prev) => {
        if (!prev?.items) return prev;
        const nextItems = prev.items.map((row) => {
          if (Number(row.cid) !== Number(cid)) return row;
          const patch = { [field]: value };
          const prediction = field === 'prediction_enabled' ? value : row.prediction_enabled;
          const fanwars = field === 'fanwars_enabled' ? value : row.fanwars_enabled;
          const mindgame = field === 'mindgame_enabled' ? value : row.mindgame_enabled;
          return {
            ...row,
            ...patch,
            arcade_active: !!(prediction || fanwars || mindgame),
          };
        });
        return { ...prev, items: nextItems };
      });

      const pending = pendingPatchRef.current[cid] || {};
      pendingPatchRef.current[cid] = { ...pending, [field]: value };

      const key = `${cid}`;
      if (patchTimersRef.current[key]) clearTimeout(patchTimersRef.current[key]);
      patchTimersRef.current[key] = setTimeout(() => {
        delete patchTimersRef.current[key];
        flushPatch(cid);
      }, PATCH_DEBOUNCE_MS);
    },
    [flushPatch]
  );

  const handleApplySearch = () => {
    setOffset(0);
    setSearch(searchInput.trim());
  };

  const handleToggleEnabledOnly = (event) => {
    setEnabledOnly(event.target.checked);
    setOffset(0);
  };

  const openToolsMenu = (event, cid) => {
    setToolsAnchor(event.currentTarget);
    setToolsCid(cid);
  };

  const closeToolsMenu = () => {
    setToolsAnchor(null);
    setToolsCid(null);
  };

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-sm-8">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      component={Link}
                      to="/admin/arcade"
                      startIcon={<ArrowBackIcon />}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      Game Dashboard
                    </Button>
                    <Typography variant="h5" component="h3" sx={{ m: 0 }}>
                      Arcade — competition settings
                    </Typography>
                    {schedulerNotes && (
                      <Tooltip title={typeof schedulerNotes === 'string' ? schedulerNotes : JSON.stringify(schedulerNotes)}>
                        <IconButton size="small" aria-label="Scheduler notes">
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Enable Prediction, FanWars, and Mind Cricket per competition. Match-level tools still use
                    match id.
                  </Typography>
                  {summaryChips}
                </div>
                <div className="col-sm-4 text-end">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={fetchList}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="Search title, abbr, or CID"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleApplySearch();
                      }}
                      sx={{ minWidth: 260 }}
                    />
                    <Button variant="contained" size="small" onClick={handleApplySearch} disabled={loading}>
                      Search
                    </Button>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={enabledOnly}
                          onChange={handleToggleEnabledOnly}
                          disabled={loading}
                        />
                      }
                      label="Enabled only (Prediction or FanWars)"
                    />
                  </Box>
                </Paper>

                <Button size="small" onClick={() => setHelpExpanded((v) => !v)} sx={{ mb: 1 }}>
                  {helpExpanded ? 'Hide' : 'Show'} help
                </Button>
                <Collapse in={helpExpanded}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Toggles upsert <code>arcade_competition_settings</code> and align existing{' '}
                    <code>arcade_matches</code> rows. New Prediction/FanWars fixtures are picked up by the
                    10-minute arcade match scheduler (12h before kickoff / while live). Mind Cricket alone does
                    not create <code>arcade_matches</code>. API: <code>GET/PATCH /admin/arcade/competitions</code>
                    .
                  </Alert>
                </Collapse>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Competition</TableCell>
                        <TableCell>CID</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Live</TableCell>
                        <TableCell align="right">Upcoming</TableCell>
                        <TableCell align="right">Open arcade</TableCell>
                        <TableCell>Prediction</TableCell>
                        <TableCell>FanWars</TableCell>
                        <TableCell>Mind Cricket</TableCell>
                        <TableCell align="center">Tools</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading && !items.length ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                            <CircularProgress size={28} />
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {!loading && !items.length ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No competitions found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {items.map((row) => {
                        const isSaving = Boolean(savingCids[row.cid]);
                        return (
                          <TableRow key={row.cid} hover selected={!!row.arcade_active}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {row.title || row.short_title || '—'}
                              </Typography>
                              {row.abbr && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {row.abbr}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {row.arcade_active && (
                                  <Chip label="Arcade active" size="small" color="success" />
                                )}
                                {row.is_default_ipl_seed && (
                                  <Chip label="IPL default" size="small" color="warning" variant="outlined" />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{row.cid}</TableCell>
                            <TableCell>
                              <Chip label={row.status || '—'} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">{row.live_match_count ?? 0}</TableCell>
                            <TableCell align="right">{row.upcoming_match_count ?? 0}</TableCell>
                            <TableCell align="right">{row.open_arcade_match_count ?? 0}</TableCell>
                            <TableCell>
                              <Switch
                                size="small"
                                checked={!!row.prediction_enabled}
                                disabled={isSaving}
                                onChange={(e) =>
                                  schedulePatch(row.cid, 'prediction_enabled', e.target.checked)
                                }
                                inputProps={{ 'aria-label': `Prediction ${row.cid}` }}
                              />
                            </TableCell>
                            <TableCell>
                              <Switch
                                size="small"
                                checked={!!row.fanwars_enabled}
                                disabled={isSaving}
                                onChange={(e) =>
                                  schedulePatch(row.cid, 'fanwars_enabled', e.target.checked)
                                }
                                inputProps={{ 'aria-label': `FanWars ${row.cid}` }}
                              />
                            </TableCell>
                            <TableCell>
                              <Switch
                                size="small"
                                checked={!!row.mindgame_enabled}
                                disabled={isSaving}
                                onChange={(e) =>
                                  schedulePatch(row.cid, 'mindgame_enabled', e.target.checked)
                                }
                                inputProps={{ 'aria-label': `Mind Cricket ${row.cid}` }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Per-match arcade tools (enter match id on each page)">
                                <IconButton
                                  size="small"
                                  onClick={(e) => openToolsMenu(e, row.cid)}
                                  aria-label={`Match tools for ${row.cid}`}
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {isSaving && <CircularProgress size={16} sx={{ ml: 0.5 }} />}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Menu anchorEl={toolsAnchor} open={Boolean(toolsAnchor)} onClose={closeToolsMenu}>
                  <MenuItem disabled sx={{ opacity: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      CID {toolsCid} — open tool, then pick a match
                    </Typography>
                  </MenuItem>
                  {MATCH_TOOL_LINKS.map((link) => (
                    <MenuItem
                      key={link.path}
                      component={Link}
                      to={link.path}
                      onClick={closeToolsMenu}
                    >
                      {link.label}
                    </MenuItem>
                  ))}
                </Menu>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {total > 0
                      ? `Showing ${offset + 1}–${offset + items.length} of ${total}`
                      : 'No results'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!hasPrev || loading}
                      onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!hasMore || loading}
                      onClick={() => setOffset((o) => o + PAGE_SIZE)}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </Component>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </div>
  );
}

export default ArcadeCompetitions;
